import { ChevronLeft, ChevronRight } from '@untitledui/icons';
import type { KeyboardEvent, ReactNode } from 'react';
import { useCallback, useState } from 'react';

import { Button } from '@/lib/base/buttons/button';
import { cx } from '@/utils/cx';

export interface CarouselSlide {
  /** Stable identifier — used as the React key and for slide labelling. */
  id: string;
  /** Slide body: an image, illustration, or any node. */
  content: ReactNode;
  /** Optional caption rendered beneath the slide. */
  caption?: ReactNode;
}

export interface CarouselProps {
  slides: CarouselSlide[];
  /** Accessible name for the carousel region (required). */
  'aria-label': string;
  /** Zero-based index of the slide shown first. Defaults to 0. */
  initialIndex?: number;
  className?: string;
}

const ROOT_CLASS =
  'flex flex-col gap-3 rounded-xl border border-[var(--color-border-secondary)] bg-[var(--color-bg-secondary)] p-3';

const VIEWPORT_CLASS =
  'overflow-hidden rounded-lg border border-[var(--color-border-secondary)] bg-[var(--color-bg-primary)]';

const CAPTION_CLASS = 'text-body-small text-[var(--color-text-secondary)]';

const COUNTER_CLASS =
  'text-body-small tabular-nums text-[var(--color-text-tertiary)]';

const DOTS_ROW_CLASS = 'flex items-center justify-center gap-1.5';

const DOT_BASE_CLASS =
  'size-2 rounded-full transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-brand-600)]';

interface CarouselDotProps {
  index: number;
  isActive: boolean;
  label: string;
  onSelect: (index: number) => void;
}

function CarouselDot({ index, isActive, label, onSelect }: CarouselDotProps) {
  const handleClick = useCallback(() => onSelect(index), [onSelect, index]);
  return (
    <button
      type='button'
      aria-label={label}
      aria-current={isActive || undefined}
      onClick={handleClick}
      className={cx(
        DOT_BASE_CLASS,
        isActive
          ? 'bg-[var(--color-brand-600)]'
          : 'bg-[var(--color-border-primary)] hover:bg-[var(--color-text-tertiary)]'
      )}
    />
  );
}

/**
 * Carousel — a slide-at-a-time viewer with previous/next controls, jump-to dots,
 * a step counter, and left/right arrow-key navigation.
 *
 * Generic: each slide's `content` is any node (image, illustration, card). The
 * component owns index state; it does not wrap around at the ends (prev/next
 * disable at the first/last slide) so it reads as ordered steps.
 */
export function Carousel({
  slides,
  'aria-label': ariaLabel,
  initialIndex = 0,
  className,
}: CarouselProps) {
  const [index, setIndex] = useState(() =>
    Math.min(Math.max(initialIndex, 0), Math.max(slides.length - 1, 0))
  );

  const count = slides.length;
  const clampedIndex = Math.min(index, Math.max(count - 1, 0));

  const goPrev = useCallback(() => {
    setIndex(Math.max(clampedIndex - 1, 0));
  }, [clampedIndex]);

  const goNext = useCallback(() => {
    setIndex(Math.min(clampedIndex + 1, count - 1));
  }, [clampedIndex, count]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goPrev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        goNext();
      }
    },
    [goPrev, goNext]
  );

  if (count === 0) {
    return null;
  }

  const activeSlide = slides[clampedIndex];

  return (
    <div
      className={cx(ROOT_CLASS, className)}
      role='group'
      aria-roledescription='carousel'
      aria-label={ariaLabel}
      aria-description='Use the left and right arrow keys to move between slides.'
      onKeyDown={handleKeyDown}
    >
      <div
        className={VIEWPORT_CLASS}
        role='group'
        aria-roledescription='slide'
        aria-label={`${clampedIndex + 1} of ${count}`}
      >
        {activeSlide.content}
      </div>

      {activeSlide.caption && (
        <p className={CAPTION_CLASS}>{activeSlide.caption}</p>
      )}

      <div className='flex items-center justify-between gap-3'>
        <Button
          color='secondary'
          size='sm'
          iconLeading={ChevronLeft}
          aria-label='Previous slide'
          isDisabled={clampedIndex === 0}
          onClick={goPrev}
        />
        <div className={DOTS_ROW_CLASS}>
          {slides.map((slide, i) => (
            <CarouselDot
              key={slide.id}
              index={i}
              isActive={i === clampedIndex}
              label={`Go to slide ${i + 1}`}
              onSelect={setIndex}
            />
          ))}
        </div>
        <div className='flex items-center gap-3'>
          <span className={COUNTER_CLASS} aria-live='polite'>
            {clampedIndex + 1} / {count}
          </span>
          <Button
            color='secondary'
            size='sm'
            iconLeading={ChevronRight}
            aria-label='Next slide'
            isDisabled={clampedIndex === count - 1}
            onClick={goNext}
          />
        </div>
      </div>
    </div>
  );
}
